/** The facilitator chrome (screens §2 GxTopBar facilitator variant, GxRail: Board · Studio · Roster) around F1, F6, F7. */
import { useCallback, useState, type ReactNode } from "react";
import type { CohortBoard } from "@bsim/contracts";
import { useSignOut } from "../auth";
import { Link } from "../shell/history";
import { Icon } from "../theme/icons";
import type { RailItem } from "../shell/Rail";
import { GxErrorBanner } from "../theme/primitives";
import { GxPill } from "../theme/GxPill";
import { useAccountItems } from "../shell/runs";
import { GxTopBar } from "../shell/TopBar";
import { GxNavSheet } from "../shell/NavSheet";
import { isMobileBand, useBand } from "../theme/breakpoints";
import { bandRound, roundInPlay, scenarioWords } from "./derive";
import { useBoardData } from "./useBoardData";
import "../shell/shell.css";
import "./facilitator.css";

/** The guided stretch as the room stands now: "Round 1 guided; all decisions from round 2" before it ends, "All decisions open" once it has (the guided rounds are over, so naming them is stale). */
const easeInWords = (easeIn: CohortBoard["cohort"]["easeIn"], roundNo: number): string => {
  const guided = { guidedR1: 1, guidedR2: 2, off: 0, guidedThroughout: Infinity }[easeIn];
  if (guided === Infinity) return "guided throughout";
  if (roundNo > guided) return "All decisions open";
  return guided === 0 ? "All decisions from round 1" : `${guided === 1 ? "Round 1 guided" : "Rounds 1 and 2 guided"}; all decisions from round ${guided + 1}`;
};


export function FacilitatorFrame({ cohortId, page, displayName, children }: {
  readonly cohortId: string; readonly page: "board" | "studio" | "roster"; readonly displayName: string; readonly children: ReactNode;
}) {
  const { board, queue, error } = useBoardData(cohortId);
  const signOut = useSignOut();
  const accountItems = useAccountItems();
  const mobile = isMobileBand(useBand());
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const base = `/f/${cohortId}`;
  const items: readonly RailItem[] = [
    { key: "board", label: "Board", icon: "board", href: `${base}/board` },
    { key: "studio", label: "Studio", icon: "studio", href: `${base}/studio` },
    { key: "roster", label: "Roster", icon: "roster", href: `${base}/roster` },
  ];
  const pre = board?.prePost.pre;
  const post = board?.prePost.post;
  const deferredCount = board !== undefined && roundInPlay(bandRound(board)) ? board.prePost.preDeferredUserIds?.length ?? 0 : 0;
  const bankName = board?.cohort.name ?? "Governexus";
  const subline = board === undefined ? "" : `${scenarioWords(board.cohort.scenarioId)} · ${easeInWords(board.cohort.easeIn, bandRound(board)?.roundNo ?? 1)}`;
  const prePostLine = (() => {
    if (board === undefined || pre === undefined || post === undefined) return null;
    const preTotal = pre.submitted + pre.inProgress + pre.notStarted;
    const postOpen = board.cohort.postAssessmentOpenedAt !== null;
    return { preTotal, full: `Pre-check ${pre.submitted} of ${preTotal}${deferredCount === 0 ? "" : ` (${deferredCount} deferred until the round ends)`} \u00b7 post ${postOpen ? `${post.submitted} of ${post.submitted + post.inProgress + post.notStarted}` : "not open"}` };
  })();
  return (
    <>
      <GxTopBar
        bankName={bankName}
        subline={subline}
        role="facilitator"
        name={displayName}
        onSignOut={signOut}
        accountItems={accountItems}
        title={items.find((i) => i.key === page)?.label ?? "Board"} onMenu={() => setMenuOpen(true)} menuOpen={menuOpen}
        clock={<a className="gx-btn gx-btn--onnavy gx-fac-roomlink" href={`/room/${cohortId}`} target="_blank" rel="noopener" data-testid="fac.openRoom"><Icon name="room" size={15} />Open room screen</a>}
      />
      <div className="gx-shell">
        {mobile ? null : <nav className="gx-rail gx-fac-rail" aria-label="Facilitator">
          <h6 className="gx-rail-h">{board?.cohort.name ?? "Cohort"}</h6>
          {items.map((it) => (
            <Link key={it.key} href={it.href} className="gx-nav" aria-current={page === it.key ? "page" : undefined}>
              <Icon name={it.icon} /><span className="gx-nav-l">{it.label}</span>
              {it.key === "board" && queue.length > 0 ? <GxPill tone="warn" testId="fac.queueBadge">{queue.length}</GxPill> : null}
            </Link>
          ))}
          <div style={{ flex: 1 }} />
          {prePostLine === null || pre === undefined ? null : (
            <div className="gx-fac-pinned" title={prePostLine.full} data-testid="fac.prepost">
              <div className="gx-small gx-faint gx-fac-pinned-full">{prePostLine.full}</div>
              <div className="gx-small gx-faint gx-fac-pinned-short gx-num" role="img" aria-label={prePostLine.full}><span className="gx-fac-pinned-lbl">Pre-check</span><span>{pre.submitted}/{prePostLine.preTotal}</span></div>
            </div>
          )}
        </nav>}
        <main className="gx-main">
          {error === undefined ? children : <GxErrorBanner details error={error} />}
          {board === undefined ? null : <footer className="gx-small gx-faint gx-fac-build" data-testid="fac.build">Engine {board.cohort.engineVersion} · content {board.cohort.contentHash.slice(0, 6)}</footer>}
        </main>
      </div>
      {menuOpen ? (
        <GxNavSheet onClose={closeMenu} bankName={bankName} subline={subline} role="facilitator" items={items} active={page}
          badges={queue.length > 0 ? { board: <GxPill tone="warn" testId="fac.queueBadge.sheet">{queue.length}</GxPill> } : {}}
          name={displayName} accountItems={accountItems} onSignOut={signOut}>
          <section aria-label="Room" className="gx-navsheet-sec">
            <a className="gx-nav" href={`/room/${cohortId}`} target="_blank" rel="noopener" data-testid="fac.openRoom.sheet"><Icon name="room" size={20} /><span className="gx-nav-l">Open room screen</span></a>
            {prePostLine === null ? null : <p className="gx-small gx-faint gx-navsheet-line" data-testid="fac.prepost.sheet">{prePostLine.full}</p>}
          </section>
        </GxNavSheet>
      ) : null}
    </>
  );
}
